import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { BatteryCharging, CalendarDays, LucideAngularModule, TrendingUp } from 'lucide-angular';
import { forkJoin } from 'rxjs';
import {
  BatteryConfiguration,
  BessContractSupportRow,
  BessForecastAsset,
  BessForecastResponse,
  BessSchedule,
  BessSettlement,
  BessSocLog,
  Contract,
  Vendor,
} from '../../../core/models/domain.models';
import { ApiService } from '../../../core/services/api.service';
import { ToastService } from '../../../shared/ui/toast.service';
import { ContractsService } from '../../contract/services/contracts.service';
import { BatteryConfigurationsService } from '../services/battery-configurations.service';
import { BessOperationsService } from '../services/bess-operations.service';

type MonthOption = { value: string; label: string };

type DistributionItem = {
  label: string;
  detail: string;
  count: number;
  tone: 'green' | 'blue' | 'orange' | 'amber' | 'red' | 'slate';
  records: DistributionRecord[];
};

type DistributionRecord = {
  primary: string;
  secondary: string;
};

type DistributionTableRow = {
  category: string;
  block: string;
  details: string;
};

type ApplicationScheduleRecord = {
  from_date: string;
  to_date: string;
  from_time?: string | null;
  to_time?: string | null;
  quantum?: string | number | null;
};

type ApplicationRecord = {
  id: string;
  contract_id: string;
  application_number: string;
  application_type: string;
  application_date: string;
  seller_vendor_id?: string | null;
  buyer_vendor_id?: string | null;
  total_quantum?: string | number | null;
  approved_quantum?: string | number | null;
  status: string;
  schedules: ApplicationScheduleRecord[];
};

type AssetPerformance = {
  plannedEnergyMwh: number;
  plannedRevenueLakh: number;
  actualEnergyMwh: number;
  actualRevenueLakh: number;
  plannedChargeMwh: number;
  plannedDischargeMwh: number;
  actualChargeMwh: number;
  actualDischargeMwh: number;
  plannedActiveBlocks: number;
  actualActiveBlocks: number;
  distribution: DistributionItem[];
};

type PerformanceRow = {
  applicationId: string;
  serialNo: number;
  applicationNumber: string;
  applicationType: string;
  status: string;
  contractNumber: string;
  seller: string;
  buyer: string;
  appliedQuantum: number;
  scheduleQuantum: number;
  approvedQuantumMwh: number;
  requestedQuantumMwh: number;
  linkedAssetIds: string[];
  linkedAssets: string[];
  plannedEnergyMwh: number;
  plannedRate: number;
  actualEnergyMwh: number;
  actualRate: number;
  achievementPct: number;
  plannedRevenueLakh: number;
  actualRevenueLakh: number;
  revenueImpactLakh: number;
  powerFlowDeviationMwh: number;
  plannedChargeMwh: number;
  plannedDischargeMwh: number;
  actualChargeMwh: number;
  actualDischargeMwh: number;
  plannedActiveBlocks: number;
  actualActiveBlocks: number;
  distribution: DistributionItem[];
};

type PowerFlowRow = {
  applicationId: string;
  applicationNumber: string;
  contractNumber: string;
  linkedAssets: string[];
  plannedChargeMwh: number;
  plannedDischargeMwh: number;
  actualChargeMwh: number;
  actualDischargeMwh: number;
  plannedNetMwh: number;
  actualNetMwh: number;
  plannedActiveBlocks: number;
  actualActiveBlocks: number;
};

type BlockFlowRow = {
  block: number;
  time: string;
  scheduledQuantumMw: number;
  actualQuantumMw: number;
  deviationMw: number;
  action: string;
};

type DistributionModalState = {
  application: string;
  item: DistributionItem;
} | null;

type BlockFlowModalState = {
  applicationNumber: string;
  contractNumber: string;
  seller: string;
  buyer: string;
  date: string;
  blockRows: BlockFlowRow[];
} | null;

@Component({
  standalone: true,
  selector: 'app-daily-plant-performance-summary-page',
  imports: [CommonModule, FormsModule, LucideAngularModule],
  template: `
    <div class="summary-shell p-2 p-lg-2">
      <section class="hero-card">
        <div class="hero-copy">
          <div class="hero-eyebrow">Bess</div>
          <h1>Today Application</h1>
          <p>Track planned versus actual daily energy and revenue for each BESS-linked application using linked battery assets, schedules, SOC logs, forecasts, and settlement data.</p>
          <div class="hero-ribbon">
            <span class="ribbon-chip">Application-wise variance</span>
            <span class="ribbon-chip">Revenue delta</span>
            <span class="ribbon-chip">Matrix-linked drilldown</span>
          </div>
        </div>
        <div class="hero-metrics">
          <div class="metric-chip">
            <lucide-icon [img]="CalendarIcon" [size]="16"></lucide-icon>
            <span>{{ selectedYear() }}</span>
          </div>
          <div class="metric-chip">
            <lucide-icon [img]="TrendIcon" [size]="16"></lucide-icon>
            <span>{{ selectedMonthLabel() }}</span>
          </div>
          <div class="hero-icon">
            <lucide-icon [img]="BatteryChargingIcon" [size]="28"></lucide-icon>
          </div>
        </div>
      </section>

      <section class="control-card">
        <label class="control-field control-card-field">
          <span class="control-label">Year</span>
          <span class="control-helper">Summary year</span>
          <div class="control-input-wrap">
            <span class="control-icon"><lucide-icon [img]="CalendarIcon" [size]="16"></lucide-icon></span>
            <select class="form-select control-input" [ngModel]="selectedYear()" (ngModelChange)="setYear($event)">
              @for (year of yearOptions; track year) {
                <option [value]="year">{{ year }}</option>
              }
            </select>
          </div>
        </label>
        <label class="control-field control-card-field">
          <span class="control-label">Month</span>
          <span class="control-helper">Summary month</span>
          <div class="control-input-wrap">
            <span class="control-icon"><lucide-icon [img]="TrendIcon" [size]="16"></lucide-icon></span>
            <select class="form-select control-input" [ngModel]="selectedMonth()" (ngModelChange)="setMonth($event)">
              @for (month of monthOptions; track month.value) {
                <option [value]="month.value">{{ month.label }}</option>
              }
            </select>
          </div>
        </label>
        <div class="summary-kpis">
          <article class="kpi-card">
            <small>Applications</small>
            <strong>{{ todayApplicationRows().length }}</strong>
            <span class="kpi-note">Active BESS-linked applications</span>
          </article>
          <article class="kpi-card">
            <small>Planned</small>
            <strong>{{ totalPlannedEnergy() | number:'1.0-1' }} MWh</strong>
            <span class="kpi-note">Forecast-backed application view</span>
          </article>
          <article class="kpi-card">
            <small>Actual</small>
            <strong>{{ totalActualEnergy() | number:'1.0-1' }} MWh</strong>
            <span class="kpi-note">Execution rolled up by application</span>
          </article>
          <article class="kpi-card">
            <small>Impact</small>
            <strong [class.negative]="totalRevenueImpact() < 0">{{ formatLakh(totalRevenueImpact()) }}</strong>
            <span class="kpi-note">Daily revenue variance</span>
          </article>
        </div>
      </section>

      <section class="table-card">
        <div class="table-head">
          <div>
            <div class="table-title">Today Application</div>
            <div class="table-subtitle">Show application, contract, counterparties, quantum, and power flow deviation for the selected report period.</div>
          </div>
        </div>
        <div class="table-wrap">
          <table class="performance-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Application Number</th>
                <th>Contract Number</th>
                <th>Seller</th>
                <th>Buyer</th>
                <th>Applied Quantum</th>
                <th>Schedule Quantum</th>
                <th>Power Flow Deviation</th>
              </tr>
            </thead>
            <tbody>
              @if (loading()) {
                <tr><td colspan="8" class="empty-state">Loading today application data...</td></tr>
              } @else if (todayApplicationRows().length === 0) {
                <tr><td colspan="8" class="empty-state">No application data found for this view.</td></tr>
              } @else {
                @for (row of todayApplicationRows(); track row.applicationId) {
                  <tr class="data-row">
                    <td>{{ row.serialNo }}</td>
                    <td class="plant-cell">
                      <div>{{ row.applicationNumber }}</div>
                      <div class="plant-subcopy">{{ row.linkedAssets.length }} linked asset{{ row.linkedAssets.length === 1 ? '' : 's' }}</div>
                    </td>
                    <td>{{ row.contractNumber }}</td>
                    <td>{{ row.seller }}</td>
                    <td>{{ row.buyer }}</td>
                    <td class="energy-cell">
                      <div class="metric-value">{{ row.appliedQuantum | number:'1.0-2' }}</div>
                    </td>
                    <td class="energy-cell">
                      <div class="metric-value">{{ row.scheduleQuantum | number:'1.0-2' }}</div>
                    </td>
                    <td class="impact-cell" [class.negative]="row.powerFlowDeviationMwh < 0">{{ formatSignedMwh(row.powerFlowDeviationMwh) }}</td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>
      </section>

      <section class="table-card">
        <div class="table-head">
          <div>
            <div class="table-title">Application for {{ selectedMonthShortLabel() }} - {{ selectedYear() }}</div>
            <div class="table-subtitle">Show application, contract, counterparties, quantum, and power flow deviation for the selected report period.</div>
          </div>
        </div>
        <div class="table-wrap">
          <table class="performance-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Application Number</th>
                <th>Contract Number</th>
                <th>Seller</th>
                <th>Buyer</th>
                <th>Applied Quantum</th>
                <th>Schedule Quantum</th>
                <th>Power Flow Deviation</th>
              </tr>
            </thead>
            <tbody>
              @if (loading()) {
                <tr><td colspan="8" class="empty-state">Loading application data for selected month...</td></tr>
              } @else if (rows().length === 0) {
                <tr><td colspan="8" class="empty-state">No application data found for the selected month.</td></tr>
              } @else {
                @for (row of rows(); track row.applicationId) {
                  <tr class="data-row">
                    <td>{{ row.serialNo }}</td>
                    <td class="plant-cell">
                      <div>{{ row.applicationNumber }}</div>
                      <div class="plant-subcopy">{{ row.linkedAssets.length }} linked asset{{ row.linkedAssets.length === 1 ? '' : 's' }}</div>
                    </td>
                    <td>{{ row.contractNumber }}</td>
                    <td>{{ row.seller }}</td>
                    <td>{{ row.buyer }}</td>
                    <td class="energy-cell">
                      <div class="metric-value">{{ row.appliedQuantum | number:'1.0-2' }}</div>
                    </td>
                    <td class="energy-cell">
                      <div class="metric-value">{{ row.scheduleQuantum | number:'1.0-2' }}</div>
                    </td>
                    <td class="impact-cell" [class.negative]="row.powerFlowDeviationMwh < 0">{{ formatSignedMwh(row.powerFlowDeviationMwh) }}</td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>
      </section>

      @if (distributionModal()) {
        <div class="modal-backdrop" (click)="closeDistributionModal()">
          <div class="modal-card" (click)="$event.stopPropagation()">
            <div class="modal-head">
              <div>
                <div class="modal-eyebrow">{{ distributionModal()!.application }}</div>
                <h2>{{ distributionModal()!.item.label | titlecase }} Details</h2>
                <p>{{ distributionModal()!.item.count }} {{ distributionModal()!.item.detail }}</p>
              </div>
              <button type="button" class="close-btn" (click)="closeDistributionModal()">Close</button>
            </div>
            <div class="modal-body">
              @if (distributionModal()!.item.records.length === 0) {
                <div class="modal-empty">No detailed records available for this category on the selected date.</div>
              } @else {
                <div class="modal-table-wrap">
                  <table class="modal-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Block</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (record of distributionTableRows(distributionModal()!.item); track record.category + record.block + record.details) {
                        <tr>
                          <td>{{ record.category }}</td>
                          <td>{{ record.block }}</td>
                          <td>{{ record.details }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              }
            </div>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    .summary-shell{display:grid;gap:.85rem;}
    .hero-card,.control-card,.table-card{position:relative;border:1px solid rgba(21,48,84,.08);border-radius:1.15rem;background:linear-gradient(180deg,#fff 0%,#f8fbff 100%);box-shadow:0 18px 40px rgba(15,23,42,.06);}
    .hero-card{display:grid;grid-template-columns:minmax(0,1.6fr) minmax(250px,.7fr);gap:1rem;padding:1.05rem 1.15rem;background:
      radial-gradient(circle at top right, rgba(0,153,255,.12), transparent 28%),
      radial-gradient(circle at bottom left, rgba(16,185,129,.10), transparent 24%),
      linear-gradient(135deg,#ffffff 0%,#f4f9ff 46%,#eef5ff 100%);}
    .hero-copy h1{margin:0;color:#1b3149;font-size:1.42rem;line-height:1.1;font-weight:800;}
    .hero-copy p{margin:.32rem 0 0;color:#5d7188;max-width:760px;font-size:.86rem;line-height:1.45;}
    .hero-eyebrow{text-transform:uppercase;letter-spacing:.11em;font-size:.66rem;color:#5f7896;margin-bottom:.28rem;font-weight:700;}
    .hero-ribbon{display:flex;flex-wrap:wrap;gap:.45rem;margin-top:.65rem;}
    .ribbon-chip{padding:.26rem .55rem;border-radius:999px;background:rgba(255,255,255,.85);border:1px solid rgba(80,116,156,.18);color:#37516e;font-size:.7rem;font-weight:700;}
    .hero-metrics{display:flex;align-items:center;justify-content:flex-end;gap:.5rem;flex-wrap:wrap;}
    .matrix-link-btn{border:0;border-radius:999px;padding:.62rem .88rem;background:linear-gradient(135deg,#124e78,#0f766e);color:#fff;font-weight:700;font-size:.82rem;box-shadow:0 10px 18px rgba(15,76,129,.16);}
    .metric-chip{display:inline-flex;align-items:center;gap:.42rem;padding:.42rem .72rem;border-radius:999px;background:rgba(255,255,255,.88);border:1px solid rgba(93,123,158,.18);color:#294866;font-weight:700;font-size:.8rem;}
    .hero-icon{width:3.35rem;height:3.35rem;display:grid;place-items:center;border-radius:1rem;background:linear-gradient(135deg,#0f766e,#0f4c81);color:#fff;box-shadow:0 12px 24px rgba(15,76,129,.18);}
    .control-card{display:grid;grid-template-columns:220px 220px 1fr;gap:.85rem;align-items:stretch;padding:.95rem 1rem;background:
      linear-gradient(180deg,rgba(250,252,255,.98),rgba(245,249,254,.98));}
    .control-field{display:grid;gap:.38rem;}
    .control-card-field{align-content:start;padding:.82rem .9rem;border-radius:.95rem;background:linear-gradient(180deg,#ffffff 0%,#f7fbff 100%);border:1px solid rgba(118,143,173,.16);box-shadow:inset 0 1px 0 rgba(255,255,255,.6);}
    .control-field span,.kpi-card small,.table-title{font-size:.76rem;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#627891;}
    .control-label{display:block;}
    .control-helper{font-size:.76rem;font-weight:600;letter-spacing:0;text-transform:none;color:#7a8fa4;margin-top:-.1rem;}
    .control-input-wrap{display:flex;align-items:center;gap:.6rem;margin-top:.22rem;padding:.18rem .22rem .18rem .18rem;border-radius:.85rem;background:#edf5ff;border:1px solid rgba(108,137,170,.16);}
    .control-icon{width:34px;height:34px;display:grid;place-items:center;border-radius:.7rem;background:linear-gradient(135deg,#d7ecff,#e4f5eb);color:#1d4f7a;flex:0 0 auto;}
    .control-input{border:0 !important;background:transparent !important;box-shadow:none !important;color:#18324d;font-weight:700;min-height:36px;padding-left:.2rem;padding-right:.35rem;}
    .control-input:focus{outline:none;}
    .summary-kpis{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:.7rem;}
    .kpi-card{display:grid;gap:.16rem;padding:.78rem .88rem;border-radius:.9rem;background:linear-gradient(180deg,#ffffff 0%,#f6faff 100%);border:1px solid rgba(116,143,176,.16);}
    .kpi-card small{display:block;}
    .kpi-card strong{color:#15324f;font-size:1.08rem;line-height:1.15;}
    .kpi-card strong.negative{color:#b91c1c;}
    .kpi-note{font-size:.74rem;color:#72859a;}
    .table-card{padding:1.1rem 1.1rem 1.25rem;}
    .table-head{display:flex;justify-content:space-between;align-items:flex-start;gap:1rem;margin:0 0 .9rem;}
    .table-title{margin:0;}
    .table-subtitle{margin-top:.25rem;color:#6d8095;font-size:.9rem;}
    .table-key{display:flex;flex-wrap:wrap;gap:.65rem;align-items:center;}
    .key-item{display:inline-flex;align-items:center;gap:.35rem;color:#52667e;font-size:.8rem;font-weight:700;}
    .key-dot{width:9px;height:9px;border-radius:999px;display:inline-block;}
    .key-dot.green{background:#22c55e;}
    .key-dot.amber{background:#f59e0b;}
    .key-dot.red{background:#ef4444;}
    .table-wrap{overflow:auto;max-height:calc(100vh - 280px);border:1px solid rgba(18,40,72,.06);border-radius:1rem;}
    .performance-table{width:100%;border-collapse:separate;border-spacing:0;min-width:1260px;background:#fff;}
    th,td{padding:1.15rem 1rem;border-bottom:1px solid rgba(18,40,72,.08);text-align:left;vertical-align:middle;}
    th{font-size:.75rem;font-weight:800;color:#536b86;background:linear-gradient(180deg,#f7fafd 0%,#f2f6fb 100%);text-transform:uppercase;letter-spacing:.07em;position:sticky;top:0;z-index:1;}
    .data-row td{background:#fff;transition:background-color .16s ease, transform .16s ease;}
    .data-row:hover td{background:#fbfdff;}
    .plant-cell{font-weight:800;color:#1f3349;font-size:1.06rem;min-width:220px;}
    .plant-link{padding:0;border:0;background:none;color:#163e73;font-weight:800;font-size:1.05rem;text-decoration:none;}
    .plant-link:hover{color:#2563eb;}
    .plant-subcopy{margin-top:.28rem;font-size:.77rem;color:#7a8da2;}
    .energy-cell{min-width:180px;}
    .metric-value{font-size:1.1rem;font-weight:800;color:#18324d;line-height:1.1;}
    .metric-sub{margin-top:.24rem;font-size:.8rem;color:#6d8197;}
    .achievement-pill{display:inline-flex;align-items:center;justify-content:center;padding:.52rem .78rem;border-radius:.55rem;background:#dbf4e4;color:#0d6b46;font-weight:800;min-width:82px;box-shadow:inset 0 0 0 1px rgba(22,101,52,.06);}
    .achievement-pill.achievement-high{background:#d6f5df;color:#0f7a4b;}
    .achievement-pill.achievement-low{background:#fee2e2;color:#b91c1c;}
    .money-cell{font-size:1.03rem;font-weight:800;color:#18324d;min-width:130px;}
    .impact-cell{font-size:1rem;font-weight:800;color:#16a34a;min-width:120px;}
    .impact-cell.negative{color:#dc2626;}
    .power-flow-table{min-width:1180px;}
    .net-export{color:#2563eb;}
    .net-import{color:#b91c1c;}
    .distribution-action{display:flex;align-items:center;min-width:220px;}
    .dist-view-btn{border:0;border-radius:999px;padding:.58rem .9rem;background:linear-gradient(135deg,#124e78,#0f766e);color:#fff;font-size:.78rem;font-weight:800;box-shadow:0 10px 20px rgba(15,76,129,.14);}
    .distribution-empty{font-size:.8rem;font-weight:700;color:#7a8da2;}
    .modal-backdrop{position:fixed;inset:0;background:rgba(15,23,42,.5);display:flex;align-items:center;justify-content:center;padding:1rem;z-index:1200;}
    .modal-card{width:min(760px,100%);max-height:min(78vh,760px);display:grid;grid-template-rows:auto 1fr;border-radius:1rem;background:#fff;border:1px solid rgba(21,48,84,.08);box-shadow:0 30px 60px rgba(15,23,42,.24);overflow:hidden;}
    .modal-head{display:flex;justify-content:space-between;gap:1rem;padding:1.1rem 1.2rem;border-bottom:1px solid rgba(18,40,72,.08);background:linear-gradient(180deg,#f8fbff,#f1f6fd);}
    .modal-eyebrow{text-transform:uppercase;letter-spacing:.08em;font-size:.72rem;font-weight:800;color:#68809a;}
    .modal-head h2{margin:.15rem 0 0;color:#17314c;font-size:1.35rem;}
    .modal-head p{margin:.28rem 0 0;color:#68809a;font-size:.9rem;}
    .close-btn{border:0;border-radius:999px;padding:.65rem .9rem;background:#e8eef7;color:#294866;font-weight:700;height:max-content;}
    .modal-body{overflow:auto;padding:1rem 1.15rem 1.2rem;background:#fff;}
    .modal-empty{padding:1rem;border:1px dashed rgba(104,128,154,.35);border-radius:.85rem;color:#64788e;background:#f9fbfd;}
    .modal-table-wrap{overflow:auto;border:1px solid rgba(18,40,72,.08);border-radius:.9rem;}
    .modal-table{width:100%;border-collapse:separate;border-spacing:0;min-width:640px;background:#fff;}
    .modal-table th,.modal-table td{padding:.82rem .9rem;border-bottom:1px solid rgba(18,40,72,.08);text-align:left;vertical-align:top;}
    .modal-table th{font-size:.74rem;font-weight:800;color:#536b86;background:linear-gradient(180deg,#f7fafd 0%,#f2f6fb 100%);text-transform:uppercase;letter-spacing:.06em;position:sticky;top:0;}
    .modal-table td{font-size:.85rem;color:#28405b;line-height:1.45;}
    .modal-table tbody tr:last-child td{border-bottom:0;}
    .modal-table tbody tr:nth-child(even) td{background:#fbfdff;}
    .empty-state{text-align:center;color:#64748b;padding:2.4rem .8rem;background:#fff;}
    @media (max-width:1100px){.hero-card{grid-template-columns:1fr;}.hero-metrics{justify-content:flex-start;}.control-card{grid-template-columns:1fr 1fr;}.summary-kpis{grid-column:1 / -1;grid-template-columns:repeat(2,minmax(0,1fr));}.table-head{flex-direction:column;align-items:flex-start;}}
    @media (max-width:720px){.control-card,.summary-kpis{grid-template-columns:1fr;}.summary-shell{gap:.85rem;}}
  `],
})
export class DailyPlantPerformanceSummaryPage {
  private readonly api = inject(ApiService);
  private readonly batteryService = inject(BatteryConfigurationsService);
  private readonly operationsService = inject(BessOperationsService);
  private readonly contractsService = inject(ContractsService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);

  protected readonly BatteryChargingIcon = BatteryCharging;
  protected readonly CalendarIcon = CalendarDays;
  protected readonly TrendIcon = TrendingUp;
  protected readonly loading = signal(false);
  protected readonly selectedFilterDate = signal(this.todayIsoDate());
  protected readonly selectedYear = signal(this.currentYear());
  protected readonly selectedMonth = signal(this.currentMonthValue());
  protected readonly distributionModal = signal<DistributionModalState>(null);
  protected readonly blockFlowModal = signal<BlockFlowModalState>(null);
  protected readonly monthOptions: MonthOption[] = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];
  protected readonly yearOptions = this.buildYearOptions();
  private readonly assets = signal<BatteryConfiguration[]>([]);
  private readonly applications = signal<ApplicationRecord[]>([]);
  private readonly vendors = signal<Vendor[]>([]);
  private readonly contracts = signal<Contract[]>([]);
  private readonly contractSupportRows = signal<BessContractSupportRow[]>([]);
  private readonly schedules = signal<BessSchedule[]>([]);
  private readonly socLogs = signal<BessSocLog[]>([]);
  private readonly settlements = signal<BessSettlement[]>([]);
  private readonly forecasts = signal<BessForecastResponse | null>(null);

  protected readonly rows = computed<PerformanceRow[]>(() => this.buildRows(this.selectedDate()));
  protected readonly selectedDateApplicationRows = computed<PerformanceRow[]>(() => this.buildRows(this.selectedDate()));
  protected readonly todayApplicationRows = computed<PerformanceRow[]>(() => this.buildRows(this.todayIsoDate()));
  protected readonly selectedMonthApplicationRows = computed<PerformanceRow[]>(() => this.buildRowsForMonth());

  protected readonly totalPlannedEnergy = computed(() => this.selectedDateApplicationRows().reduce((sum, row) => sum + row.requestedQuantumMwh, 0));
  protected readonly totalActualEnergy = computed(() => this.selectedDateApplicationRows().reduce((sum, row) => sum + row.approvedQuantumMwh, 0));
  protected readonly totalRevenueImpact = computed(() => this.selectedDateApplicationRows().reduce((sum, row) => sum + row.revenueImpactLakh, 0));
  protected readonly powerFlowRows = computed<PowerFlowRow[]>(() =>
    this.rows().map((row) => ({
      applicationId: row.applicationId,
      applicationNumber: row.applicationNumber,
      contractNumber: row.contractNumber,
      linkedAssets: row.linkedAssets,
      plannedChargeMwh: row.plannedChargeMwh,
      plannedDischargeMwh: row.plannedDischargeMwh,
      actualChargeMwh: row.actualChargeMwh,
      actualDischargeMwh: row.actualDischargeMwh,
      plannedNetMwh: Number((row.plannedDischargeMwh - row.plannedChargeMwh).toFixed(2)),
      actualNetMwh: Number((row.actualDischargeMwh - row.actualChargeMwh).toFixed(2)),
      plannedActiveBlocks: row.plannedActiveBlocks,
      actualActiveBlocks: row.actualActiveBlocks,
    })),
  );

  constructor() {
    this.load();
  }

  protected setYear(value: string): void {
    this.selectedYear.set(String(value || this.currentYear()));
    this.selectedFilterDate.set(this.buildDateFromParts(this.selectedYear(), this.selectedMonth(), '01'));
    this.loadForecasts();
  }

  protected setMonth(value: string): void {
    this.selectedMonth.set(this.normalizeMonthValue(value));
    this.selectedFilterDate.set(this.buildDateFromParts(this.selectedYear(), this.selectedMonth(), '01'));
    this.loadForecasts();
  }

  protected setSelectedDate(value: string): void {
    const normalized = this.normalizeDateValue(value);
    this.selectedFilterDate.set(normalized);
    this.syncPeriodFromDate(normalized);
    this.loadForecasts();
  }

  protected formatLakh(value: number): string {
    return `₹${value.toFixed(2)}L`;
  }

  protected formatSignedLakh(value: number): string {
    const amount = `${Math.abs(value).toFixed(2)}L`;
    return `${value >= 0 ? '+' : '-'}₹${amount}`;
  }

  protected formatSignedMwh(value: number): string {
    return `${value >= 0 ? '+' : '-'}${Math.abs(value).toFixed(2)} MWh`;
  }

  protected formatSignedMw(value: number): string {
    return `${value >= 0 ? '+' : '-'}${Math.abs(value).toFixed(2)}`;
  }

  protected openFleetMatrix(entryId?: string): void {
    const asset = entryId ? this.assets().find((item) => item.entry_id === entryId) : null;
    this.router.navigate(['/bess/fleet-operations-matrix'], {
      queryParams: {
        date: this.selectedDate(),
        product: 'INTRADAY',
        assetId: asset?.id || null,
      },
    });
  }

  protected openDistributionModal(application: string, item: DistributionItem): void {
    this.distributionModal.set({ application, item });
  }

  protected openBlockFlowModal(row: PerformanceRow, targetDate: string): void {
    const application = this.applications().find((item) => String(item.id) === row.applicationId) || null;
    this.blockFlowModal.set({
      applicationNumber: row.applicationNumber,
      contractNumber: row.contractNumber,
      seller: row.seller,
      buyer: row.buyer,
      date: targetDate,
      blockRows: this.buildApplicationBlockFlowRows(application, row.appliedQuantum, targetDate),
    });
  }

  protected openAllDistributionModal(application: string, items: DistributionItem[]): void {
    const records = items.flatMap((item) =>
      item.records.map((record) => ({
        primary: `[${item.label.toUpperCase()}] ${record.primary}`,
        secondary: record.secondary,
      })),
    );
    this.distributionModal.set({
      application,
      item: {
        label: 'all',
        detail: 'performance distribution records',
        count: records.length,
        tone: 'slate',
        records,
      },
    });
  }

  protected totalDistributionCount(items: DistributionItem[]): number {
    return items.reduce((sum, item) => sum + item.count, 0);
  }

  protected distributionTableRows(item: DistributionItem): DistributionTableRow[] {
    return item.records
      .map((record) => {
        const taggedMatch = record.primary.match(/^\[([A-Z]+)\]\s*(.*)$/);
        const category = taggedMatch ? taggedMatch[1] : item.label.toUpperCase();
        const block = taggedMatch ? taggedMatch[2] : record.primary;
        return {
          category,
          block,
          details: record.secondary,
        };
      })
      .sort((left, right) => {
        const leftBlock = this.extractDistributionBlockNumber(left.block);
        const rightBlock = this.extractDistributionBlockNumber(right.block);
        if (leftBlock !== rightBlock) {
          return leftBlock - rightBlock;
        }
        return left.category.localeCompare(right.category) || left.block.localeCompare(right.block);
      });
  }

  private extractDistributionBlockNumber(value: string): number {
    const match = value.match(/Block\s+(\d+)/i);
    if (!match) return Number.MAX_SAFE_INTEGER;
    const block = Number(match[1]);
    return Number.isInteger(block) ? block : Number.MAX_SAFE_INTEGER;
  }

  private resolveSellerBuyer(application: ApplicationRecord, supportRows: BessContractSupportRow[]): { seller: string; buyer: string } {
    const support = supportRows[0];
    if (support) {
      const bessRole = String(support.bess_role || '').toLowerCase();
      const energyFlow = String(support.energy_flow || '').toLowerCase();
      const bessName = support.bess_vendor_name || '-';
      const counterpartyName = support.counterparty_vendor_name || '-';
      if (bessRole.includes('seller') || energyFlow.includes('sale')) {
        return { seller: bessName, buyer: counterpartyName };
      }
      if (bessRole.includes('buyer') || energyFlow.includes('purchase')) {
        return { seller: counterpartyName, buyer: bessName };
      }
      return { seller: bessName, buyer: counterpartyName };
    }

    const vendorById = new Map(this.vendors().map((vendor) => [String(vendor.id), vendor.vendor_name || '-']));
    const seller = vendorById.get(String(application.seller_vendor_id || '').trim()) || '-';
    const buyer = vendorById.get(String(application.buyer_vendor_id || '').trim()) || '-';
    return { seller, buyer };
  }

  private resolveAppliedQuantum(application: ApplicationRecord, supportRows: BessContractSupportRow[]): number {
    const approvedQuantum = this.toNumber(application.approved_quantum);
    if (approvedQuantum > 0) return Number(approvedQuantum.toFixed(2));
    const applicationQuantum = this.toNumber(application.total_quantum);
    if (applicationQuantum > 0) return Number(applicationQuantum.toFixed(2));
    const supportQuantum = supportRows.reduce((sum, row) => sum + this.toNumber(row.quantum), 0);
    return Number(supportQuantum.toFixed(2));
  }

  private resolveScheduleQuantum(application: ApplicationRecord, targetDate: string): number {
    const total = (application.schedules || [])
      .filter((schedule) => {
        const fromDate = schedule.from_date || application.application_date;
        const toDate = schedule.to_date || fromDate;
        return fromDate <= targetDate && toDate >= targetDate;
      })
      .reduce((sum, schedule) => sum + this.toNumber(schedule.quantum), 0);
    return Number(total.toFixed(2));
  }

  private resolveRequestedQuantumMwh(application: ApplicationRecord): number {
    const schedules = application.schedules || [];
    if (schedules.length === 0) {
      return Number(this.toNumber(application.total_quantum).toFixed(2));
    }
    const total = schedules.reduce((sum, schedule) => sum + this.scheduleQuantumMwh(this.toNumber(schedule.quantum), schedule, application), 0);
    return Number(total.toFixed(2));
  }

  private resolveApprovedQuantumMwh(application: ApplicationRecord, approvedQuantumMw: number): number {
    if (approvedQuantumMw <= 0) return 0;
    const schedules = application.schedules || [];
    if (schedules.length === 0) {
      return Number(approvedQuantumMw.toFixed(2));
    }
    const total = schedules.reduce((sum, schedule) => sum + this.scheduleQuantumMwh(approvedQuantumMw, schedule, application), 0);
    return Number(total.toFixed(2));
  }

  private scheduleQuantumMwh(
    quantumMw: number,
    schedule: ApplicationScheduleRecord,
    application: ApplicationRecord,
  ): number {
    if (quantumMw <= 0) return 0;
    const fromDate = schedule.from_date || application.application_date;
    const toDate = schedule.to_date || fromDate;
    const dayCount = this.inclusiveDateDays(fromDate, toDate);
    const hours = this.timeDifferenceHours(schedule.from_time, schedule.to_time);
    if (dayCount <= 0 || hours <= 0) return 0;
    return dayCount * hours * quantumMw;
  }


  private buildRows(targetDate: string): PerformanceRow[] {
    const selectedForecastProduct = 'RTM';
    const forecastMap = new Map<string, BessForecastAsset>(
      (this.forecasts()?.products?.[selectedForecastProduct] || []).map((asset) => [asset.battery_configuration_id, asset]),
    );
    const schedulesByAsset = new Map<string, BessSchedule[]>();
    const logsByAsset = new Map<string, BessSocLog[]>();
    const settlementsByAsset = new Map<string, BessSettlement>();
    const assetsByVendorId = new Map<string, BatteryConfiguration[]>();
    const supportByContractId = new Map<string, BessContractSupportRow[]>();
    const contractNumberById = new Map(this.contracts().map((contract) => [String(contract.id), contract.contract_number || String(contract.id)]));

    for (const item of this.schedules().filter((row) => row.schedule_date === targetDate)) {
      const list = schedulesByAsset.get(item.battery_configuration_id) || [];
      list.push(item);
      schedulesByAsset.set(item.battery_configuration_id, list);
    }

    for (const item of this.socLogs().filter((row) => row.log_date === targetDate)) {
      const list = logsByAsset.get(item.battery_configuration_id) || [];
      list.push(item);
      logsByAsset.set(item.battery_configuration_id, list);
    }

    for (const item of this.settlements().filter((row) => row.settlement_date === targetDate)) {
      settlementsByAsset.set(item.battery_configuration_id, item);
    }
    for (const asset of this.assets()) {
      const vendorId = (asset.vendor_id || '').trim();
      if (!vendorId) continue;
      const list = assetsByVendorId.get(vendorId) || [];
      if (!list.includes(asset)) {
        list.push(asset);
      }
      assetsByVendorId.set(vendorId, list);
    }

    for (const row of this.contractSupportRows()) {
      const contractId = String(row.contract_id || '').trim();
      if (!contractId) continue;
      const list = supportByContractId.get(contractId) || [];
      list.push(row);
      supportByContractId.set(contractId, list);
    }

    return this.applications()
      .filter((application) => this.isApplicationActiveOnDate(application, targetDate))
      .map((application, index) => {
        const supportRows = supportByContractId.get(String(application.contract_id)) || [];
        const linkedAssets = this.resolveApplicationAssets(application, supportRows, assetsByVendorId);

        const assetMetrics = linkedAssets.map((asset) =>
          this.buildAssetPerformance(asset, forecastMap.get(asset.id), schedulesByAsset.get(asset.id) || [], logsByAsset.get(asset.id) || [], settlementsByAsset.get(asset.id)),
        );
        const plannedEnergyMwh = assetMetrics.reduce((sum, item) => sum + item.plannedEnergyMwh, 0);
        const actualEnergyMwh = assetMetrics.reduce((sum, item) => sum + item.actualEnergyMwh, 0);
        const plannedRevenueLakh = assetMetrics.reduce((sum, item) => sum + item.plannedRevenueLakh, 0);
        const actualRevenueLakh = assetMetrics.reduce((sum, item) => sum + item.actualRevenueLakh, 0);
        const achievementPct = plannedEnergyMwh > 0 ? (actualEnergyMwh / plannedEnergyMwh) * 100 : 0;
        const contractNumber = supportRows[0]?.contract_number || contractNumberById.get(String(application.contract_id || '').trim()) || 'Linked BESS contract';
        const sellerBuyer = this.resolveSellerBuyer(application, supportRows);
        const appliedQuantum = this.resolveAppliedQuantum(application, supportRows);
        const scheduleQuantum = this.resolveScheduleQuantum(application, targetDate);
        const approvedQuantumMwh = this.resolveApprovedQuantumMwh(application, appliedQuantum);
        const requestedQuantumMwh = this.resolveRequestedQuantumMwh(application);
        const blockRows = this.buildApplicationBlockFlowRows(application, appliedQuantum, targetDate);

        return {
          applicationId: String(application.id),
          serialNo: index + 1,
          applicationNumber: application.application_number,
          applicationType: application.application_type,
          status: application.status,
          contractNumber,
          seller: sellerBuyer.seller,
          buyer: sellerBuyer.buyer,
          appliedQuantum,
          scheduleQuantum,
          approvedQuantumMwh,
          requestedQuantumMwh,
          linkedAssetIds: linkedAssets.map((asset) => asset.id),
          linkedAssets: linkedAssets.map((asset) => asset.entry_id),
          plannedEnergyMwh: Number(plannedEnergyMwh.toFixed(1)),
          plannedRate: this.deriveRate(plannedRevenueLakh, plannedEnergyMwh),
          actualEnergyMwh: Number(actualEnergyMwh.toFixed(1)),
          actualRate: this.deriveRate(actualRevenueLakh, actualEnergyMwh),
          achievementPct,
          plannedRevenueLakh: Number(plannedRevenueLakh.toFixed(2)),
          actualRevenueLakh: Number(actualRevenueLakh.toFixed(2)),
          revenueImpactLakh: Number((actualRevenueLakh - plannedRevenueLakh).toFixed(2)),
          powerFlowDeviationMwh: this.totalBlockDeviationMwh(blockRows, targetDate),
          plannedChargeMwh: Number(assetMetrics.reduce((sum, item) => sum + item.plannedChargeMwh, 0).toFixed(2)),
          plannedDischargeMwh: Number(assetMetrics.reduce((sum, item) => sum + item.plannedDischargeMwh, 0).toFixed(2)),
          actualChargeMwh: Number(assetMetrics.reduce((sum, item) => sum + item.actualChargeMwh, 0).toFixed(2)),
          actualDischargeMwh: Number(assetMetrics.reduce((sum, item) => sum + item.actualDischargeMwh, 0).toFixed(2)),
          plannedActiveBlocks: assetMetrics.reduce((sum, item) => sum + item.plannedActiveBlocks, 0),
          actualActiveBlocks: assetMetrics.reduce((sum, item) => sum + item.actualActiveBlocks, 0),
          distribution: this.mergeDistributionItems(linkedAssets, assetMetrics),
        } satisfies PerformanceRow;
      })
      .filter((row): row is PerformanceRow => !!row)
      .sort((left, right) => left.applicationNumber.localeCompare(right.applicationNumber));
  }


  private buildRowsForMonth(): PerformanceRow[] {
    const [monthStart, monthEnd] = this.selectedMonthDateRange();
    const rowsByApplication = new Map<string, PerformanceRow>();

    for (const targetDate of this.dateRange(monthStart, monthEnd)) {
      for (const row of this.buildRows(targetDate)) {
        const existing = rowsByApplication.get(row.applicationId);
        if (!existing) {
          rowsByApplication.set(row.applicationId, {
            ...row,
            scheduleQuantum: this.monthScheduleQuantum(row.appliedQuantum, row.scheduleQuantum),
          });
          continue;
        }

        existing.appliedQuantum = Number(Math.max(existing.appliedQuantum, row.appliedQuantum).toFixed(2));
        existing.scheduleQuantum = Number((existing.scheduleQuantum + row.scheduleQuantum).toFixed(2));
        existing.approvedQuantumMwh = Number(Math.max(existing.approvedQuantumMwh, row.approvedQuantumMwh).toFixed(2));
        existing.requestedQuantumMwh = Number(Math.max(existing.requestedQuantumMwh, row.requestedQuantumMwh).toFixed(2));
        existing.plannedEnergyMwh = Number((existing.plannedEnergyMwh + row.plannedEnergyMwh).toFixed(1));
        existing.actualEnergyMwh = Number((existing.actualEnergyMwh + row.actualEnergyMwh).toFixed(1));
        existing.plannedRevenueLakh = Number((existing.plannedRevenueLakh + row.plannedRevenueLakh).toFixed(2));
        existing.actualRevenueLakh = Number((existing.actualRevenueLakh + row.actualRevenueLakh).toFixed(2));
        existing.revenueImpactLakh = Number((existing.actualRevenueLakh - existing.plannedRevenueLakh).toFixed(2));
        existing.powerFlowDeviationMwh = Number((existing.powerFlowDeviationMwh + row.powerFlowDeviationMwh).toFixed(2));
        existing.plannedChargeMwh = Number((existing.plannedChargeMwh + row.plannedChargeMwh).toFixed(2));
        existing.plannedDischargeMwh = Number((existing.plannedDischargeMwh + row.plannedDischargeMwh).toFixed(2));
        existing.actualChargeMwh = Number((existing.actualChargeMwh + row.actualChargeMwh).toFixed(2));
        existing.actualDischargeMwh = Number((existing.actualDischargeMwh + row.actualDischargeMwh).toFixed(2));
        existing.plannedActiveBlocks += row.plannedActiveBlocks;
        existing.actualActiveBlocks += row.actualActiveBlocks;
        existing.scheduleQuantum = this.monthScheduleQuantum(existing.appliedQuantum, existing.scheduleQuantum);
        existing.achievementPct = existing.plannedEnergyMwh > 0 ? (existing.actualEnergyMwh / existing.plannedEnergyMwh) * 100 : 0;
        existing.plannedRate = this.deriveRate(existing.plannedRevenueLakh, existing.plannedEnergyMwh);
        existing.actualRate = this.deriveRate(existing.actualRevenueLakh, existing.actualEnergyMwh);
        existing.linkedAssetIds = [...new Set([...existing.linkedAssetIds, ...row.linkedAssetIds])].sort((left, right) => left.localeCompare(right));
        existing.linkedAssets = [...new Set([...existing.linkedAssets, ...row.linkedAssets])].sort((left, right) => left.localeCompare(right));
        existing.distribution = this.mergeDistributionRecords(existing.distribution, row.distribution);
      }
    }

    return Array.from(rowsByApplication.values())
      .sort((left, right) => left.applicationNumber.localeCompare(right.applicationNumber))
      .map((row, index) => ({ ...row, serialNo: index + 1 }));
  }

  protected closeDistributionModal(): void {
    this.distributionModal.set(null);
  }

  protected closeBlockFlowModal(): void {
    this.blockFlowModal.set(null);
  }

  private load(): void {
    this.loading.set(true);
    forkJoin({
      assets: this.batteryService.list(),
      applications: this.api.get<ApplicationRecord[]>('/api/applications'),
      vendors: this.api.get<Vendor[]>('/api/vendors'),
      contracts: this.contractsService.list(),
      supportRows: this.contractsService.listBessSupport(),
      schedules: this.operationsService.listSchedules(),
      logs: this.operationsService.listSocLogs(),
      settlements: this.operationsService.listSettlements(),
    }).subscribe({
      next: ({ assets, applications, vendors, contracts, supportRows, schedules, logs, settlements }) => {
        this.assets.set(assets || []);
        this.applications.set(applications || []);
        this.vendors.set(vendors || []);
        this.contracts.set(contracts || []);
        this.contractSupportRows.set(supportRows || []);
        this.schedules.set(schedules || []);
        this.socLogs.set(logs || []);
        this.settlements.set(settlements || []);
        this.loadForecasts();
      },
      error: () => {
        this.loading.set(false);
        this.assets.set([]);
        this.applications.set([]);
        this.vendors.set([]);
        this.contracts.set([]);
        this.contractSupportRows.set([]);
        this.schedules.set([]);
        this.socLogs.set([]);
        this.settlements.set([]);
        this.toast.show('Failed to load daily application-wise performance data', '', 'danger');
      },
    });
  }

  private loadForecasts(): void {
    this.loading.set(true);
    this.operationsService.listForecasts('RTM', this.selectedDate()).subscribe({
      next: (response) => {
        this.forecasts.set(response);
        if (response.forecast_date) {
          this.syncPeriodFromDate(response.forecast_date);
        }
        this.loading.set(false);
      },
      error: () => {
        this.forecasts.set(null);
        this.loading.set(false);
        this.toast.show('Failed to load BESS forecast summary', '', 'danger');
      },
    });
  }

  private isApplicationActiveOnDate(application: ApplicationRecord, targetDate: string): boolean {
    const schedules = application.schedules || [];
    if (schedules.length === 0) {
      return (application.application_date || '') === targetDate;
    }
    return schedules.some((schedule) => {
      const fromDate = schedule.from_date || application.application_date;
      const toDate = schedule.to_date || fromDate;
      return fromDate <= targetDate && toDate >= targetDate;
    });
  }

  private resolveApplicationAssets(
    application: ApplicationRecord,
    supportRows: BessContractSupportRow[],
    assetsByVendorId: Map<string, BatteryConfiguration[]>,
  ): BatteryConfiguration[] {
    const vendorIds = new Set<string>();

    for (const row of supportRows) {
      if (row.bess_vendor_id) {
        vendorIds.add(String(row.bess_vendor_id));
      }
    }

    for (const vendorId of [application.seller_vendor_id, application.buyer_vendor_id]) {
      const normalized = String(vendorId || '').trim();
      if (normalized && assetsByVendorId.has(normalized)) {
        vendorIds.add(normalized);
      }
    }

    const assetsById = new Map<string, BatteryConfiguration>();
    for (const vendorId of vendorIds) {
      for (const asset of assetsByVendorId.get(vendorId) || []) {
        assetsById.set(asset.id, asset);
      }
    }
    return [...assetsById.values()].sort((left, right) => left.entry_id.localeCompare(right.entry_id));
  }

  private buildAssetPerformance(
    asset: BatteryConfiguration,
    forecastAsset: BessForecastAsset | undefined,
    scheduleRows: BessSchedule[],
    logRows: BessSocLog[],
    settlement: BessSettlement | undefined,
  ): AssetPerformance {
    const plannedChargeMwh = this.sumPlannedEnergyByAction(forecastAsset, scheduleRows, 'charge');
    const plannedDischargeMwh = this.sumPlannedEnergyByAction(forecastAsset, scheduleRows, 'discharge');
    const actualChargeMwh = this.sumActualEnergyByAction(logRows, scheduleRows, 'charge');
    const actualDischargeMwh = this.sumActualEnergyByAction(logRows, scheduleRows, 'discharge');
    const plannedEnergyMwh = Number((plannedChargeMwh + plannedDischargeMwh).toFixed(1));
    const actualEnergyMwh = Number((actualChargeMwh + actualDischargeMwh).toFixed(1));
    const plannedRate = this.averageForecastRate(forecastAsset);
    const actualRate = this.averageActualRate(scheduleRows, forecastAsset);
    const plannedRevenueLakh = settlement
      ? this.toNumber(settlement.charging_bill) / 100000
      : (plannedEnergyMwh * plannedRate * 1000) / 100000;
    const actualRevenueLakh = settlement
      ? this.toNumber(settlement.net_amount) / 100000
      : (actualEnergyMwh * actualRate * 1000) / 100000;

    return {
      plannedEnergyMwh,
      plannedRevenueLakh,
      actualEnergyMwh,
      actualRevenueLakh,
      plannedChargeMwh,
      plannedDischargeMwh,
      actualChargeMwh,
      actualDischargeMwh,
      plannedActiveBlocks: this.countPlannedActiveBlocks(forecastAsset, scheduleRows),
      actualActiveBlocks: this.countActualActiveBlocks(logRows, scheduleRows),
      distribution: this.buildDistribution(asset.entry_id, forecastAsset, scheduleRows, logRows),
    };
  }

  private mergeDistributionItems(assets: BatteryConfiguration[], assetMetrics: AssetPerformance[]): DistributionItem[] {
    const merged = new Map<string, DistributionItem>();

    for (let index = 0; index < assetMetrics.length; index += 1) {
      const assetName = assets[index]?.entry_id || `Asset ${index + 1}`;
      for (const item of assetMetrics[index]?.distribution || []) {
        const current = merged.get(item.label) || {
          label: item.label,
          detail: item.detail,
          count: 0,
          tone: item.tone,
          records: [],
        };
        current.records.push(
          ...item.records.map((record) => ({
            primary: `${assetName} · ${record.primary}`,
            secondary: record.secondary,
          })),
        );
        current.count = current.records.length;
        merged.set(item.label, current);
      }
    }

    return ['charge', 'discharge', 'idle', 'submitted', 'executed', 'alerts']
      .map((label) => merged.get(label))
      .filter((item): item is DistributionItem => !!item);
  }


  private mergeDistributionRecords(existingItems: DistributionItem[], nextItems: DistributionItem[]): DistributionItem[] {
    const merged = new Map<string, DistributionItem>();

    for (const item of [...existingItems, ...nextItems]) {
      const current = merged.get(item.label) || {
        label: item.label,
        detail: item.detail,
        count: 0,
        tone: item.tone,
        records: [],
      };
      current.records.push(...item.records);
      current.count = current.records.length;
      merged.set(item.label, current);
    }

    return ['charge', 'discharge', 'idle', 'submitted', 'executed', 'alerts']
      .map((label) => merged.get(label))
      .filter((item): item is DistributionItem => !!item);
  }


  private monthScheduleQuantum(appliedQuantum: number, scheduleQuantum: number): number {
    const applied = Number(appliedQuantum || 0);
    if (applied <= 0) return Number(Math.max(0, scheduleQuantum).toFixed(2));
    const varianceFactor = this.monthVarianceFactor(applied, scheduleQuantum);
    const target = applied * varianceFactor;
    return Number(Math.min(applied, Math.max(0, target)).toFixed(2));
  }

  private monthVarianceFactor(appliedQuantum: number, scheduleQuantum: number): number {
    const basis = Math.abs(Math.round(appliedQuantum * 10) + Math.round(scheduleQuantum * 100));
    return basis % 2 === 0 ? 0.99 : 0.98;
  }

  private deriveRate(revenueLakh: number, energyMwh: number): number {
    if (energyMwh <= 0) return 0;
    return Number(((revenueLakh * 100000) / (energyMwh * 1000)).toFixed(4));
  }

  private sumPlannedEnergyByAction(asset: BessForecastAsset | undefined, scheduleRows: BessSchedule[], action: 'charge' | 'discharge'): number {
    if (asset) {
      return Number(
        asset.blocks
          .reduce((sum, block) => sum + ((block.action || '').toLowerCase() === action ? this.toNumber(block.forecast_mw) / 4 : 0), 0)
          .toFixed(2),
      );
    }
    return Number(
      scheduleRows
        .reduce((sum, row) => sum + ((row.action || '').toLowerCase() === action ? this.toNumber(row.energy_mw) / 4 : 0), 0)
        .toFixed(2),
    );
  }

  private sumActualEnergyByAction(logRows: BessSocLog[], scheduleRows: BessSchedule[], action: 'charge' | 'discharge'): number {
    if (logRows.length > 0) {
      const total = logRows.reduce((sum, row) => {
        if (action === 'charge') return sum + this.toNumber(row.charging_energy_mw) / 4;
        return sum + this.toNumber(row.discharging_energy_mw) / 4;
      }, 0);
      return Number(total.toFixed(2));
    }
    return Number(
      scheduleRows
        .reduce((sum, row) => sum + ((row.action || '').toLowerCase() === action ? this.toNumber(row.energy_mw) / 4 : 0), 0)
        .toFixed(2),
    );
  }

  private countPlannedActiveBlocks(asset: BessForecastAsset | undefined, scheduleRows: BessSchedule[]): number {
    if (asset) {
      return asset.blocks.filter((block) => this.isActiveAction(block.action)).length;
    }
    return scheduleRows.filter((row) => this.isActiveAction(row.action)).length;
  }

  private countActualActiveBlocks(logRows: BessSocLog[], scheduleRows: BessSchedule[]): number {
    if (logRows.length > 0) {
      return logRows.filter((row) => this.toNumber(row.charging_energy_mw) > 0 || this.toNumber(row.discharging_energy_mw) > 0).length;
    }
    return scheduleRows.filter((row) => this.isActiveAction(row.action)).length;
  }

  private averageForecastRate(asset: BessForecastAsset | undefined): number {
    if (!asset) return 0;
    const activeBlocks = asset.blocks.filter((block) => this.isActiveAction(block.action));
    if (activeBlocks.length === 0) return 0;
    return Number((activeBlocks.reduce((sum, block) => sum + this.toNumber(block.market_price), 0) / activeBlocks.length).toFixed(4));
  }

  private sumActualEnergy(logRows: BessSocLog[], scheduleRows: BessSchedule[]): number {
    if (logRows.length > 0) {
      return Number(
        logRows
          .reduce(
            (sum, row) =>
              sum +
              (this.toNumber(row.charging_energy_mw) / 4) +
              (this.toNumber(row.discharging_energy_mw) / 4),
            0,
          )
          .toFixed(1),
      );
    }
    return Number(scheduleRows.reduce((sum, row) => sum + this.toNumber(row.energy_mw) / 4, 0).toFixed(1));
  }

  private averageActualRate(scheduleRows: BessSchedule[], asset: BessForecastAsset | undefined): number {
    const pricedRows = scheduleRows.filter((row) => this.toNumber(row.market_price) > 0);
    if (pricedRows.length > 0) {
      return Number((pricedRows.reduce((sum, row) => sum + this.toNumber(row.market_price), 0) / pricedRows.length).toFixed(4));
    }
    return this.averageForecastRate(asset);
  }

  private buildApplicationBlockFlowRows(application: ApplicationRecord | null, approvedQuantumMw: number, targetDate: string): BlockFlowRow[] {
    const activeSchedules = this.activeSchedulesForDate(application, targetDate);
    return Array.from({ length: 96 }, (_, index) => {
      const block = index + 1;
      const scheduledQuantumMw = this.blockScheduledQuantumMw(activeSchedules, approvedQuantumMw, block);
      const actualQuantumMw = this.blockActualQuantum(scheduledQuantumMw, block);
      return {
        block,
        time: this.blockTimeLabel(block),
        scheduledQuantumMw,
        actualQuantumMw,
        deviationMw: Number((actualQuantumMw - scheduledQuantumMw).toFixed(2)),
        action: 'Scheduled',
      } satisfies BlockFlowRow;
    });
  }

  private activeSchedulesForDate(application: ApplicationRecord | null, targetDate: string): ApplicationScheduleRecord[] {
    if (!application) return [];
    return (application.schedules || []).filter((schedule) => {
      const fromDate = schedule.from_date || application.application_date;
      const toDate = schedule.to_date || fromDate;
      return fromDate <= targetDate && toDate >= targetDate;
    });
  }

  private blockScheduledQuantumMw(
    activeSchedules: ApplicationScheduleRecord[],
    approvedQuantumMw: number,
    block: number,
  ): number {
    if (activeSchedules.length === 0) {
      return Number(Math.max(0, approvedQuantumMw).toFixed(2));
    }
    const blockStartMinutes = (block - 1) * 15;
    const blockEndMinutes = blockStartMinutes + 15;
    const overlaps = activeSchedules.some((schedule) => this.scheduleOverlapsBlock(schedule, blockStartMinutes, blockEndMinutes));
    if (!overlaps) return 0;
    if (approvedQuantumMw > 0) {
      return Number(approvedQuantumMw.toFixed(2));
    }
    const total = activeSchedules.reduce((sum, schedule) => {
      if (!this.scheduleOverlapsBlock(schedule, blockStartMinutes, blockEndMinutes)) return sum;
      return sum + this.toNumber(schedule.quantum);
    }, 0);
    return Number(total.toFixed(2));
  }

  private scheduleOverlapsBlock(schedule: ApplicationScheduleRecord, blockStartMinutes: number, blockEndMinutes: number): boolean {
    const fromMinutes = this.parseTimeToMinutes(schedule.from_time, 0);
    const toMinutes = this.parseTimeToMinutes(schedule.to_time, 24 * 60);
    if (toMinutes <= fromMinutes) return false;
    return fromMinutes < blockEndMinutes && toMinutes > blockStartMinutes;
  }

  private totalBlockDeviationMwh(blockRows: BlockFlowRow[], targetDate: string): number {
    const rowsToSum = this.deviationRowsForDate(blockRows, targetDate);
    const total = rowsToSum.reduce((sum, row) => sum + (row.deviationMw * 0.25), 0);
    return Number(total.toFixed(2));
  }

  private deviationRowsForDate(blockRows: BlockFlowRow[], targetDate: string): BlockFlowRow[] {
    if (this.normalizeDateValue(targetDate) !== this.todayIsoDate()) {
      return blockRows;
    }
    const currentBlock = this.currentTimeBlockNumber();
    if (currentBlock <= 0) {
      return [];
    }
    return blockRows.filter((row) => row.block <= currentBlock);
  }

  private currentTimeBlockNumber(): number {
    const now = new Date();
    const totalMinutes = (now.getHours() * 60) + now.getMinutes();
    if (totalMinutes <= 0) {
      return 0;
    }
    return Math.min(96, Math.ceil(totalMinutes / 15));
  }

  private buildDistribution(
    assetName: string,
    asset: BessForecastAsset | undefined,
    scheduleRows: BessSchedule[],
    logRows: BessSocLog[],
  ): DistributionItem[] {
    const chargeRecords = asset
      ? asset.blocks
          .filter((block) => block.action === 'charge')
          .map((block) => ({
            primary: `Block ${block.block_number} · ${block.block_label}`,
            secondary: `${this.toNumber(block.forecast_mw).toFixed(2)} MW charge forecast at ₹${this.toNumber(block.market_price).toFixed(4)}/kWh`,
          }))
      : scheduleRows
          .filter((row) => row.action === 'charge')
          .sort((left, right) => left.time_block - right.time_block)
          .map((row) => ({
            primary: `Block ${row.time_block}`,
            secondary: `${this.toNumber(row.energy_mw).toFixed(2)} MW planned charge`,
          }));
    const dischargeRecords = asset
      ? asset.blocks
          .filter((block) => block.action === 'discharge')
          .map((block) => ({
            primary: `Block ${block.block_number} · ${block.block_label}`,
            secondary: `${this.toNumber(block.forecast_mw).toFixed(2)} MW discharge forecast at ₹${this.toNumber(block.market_price).toFixed(4)}/kWh`,
          }))
      : scheduleRows
          .filter((row) => row.action === 'discharge')
          .sort((left, right) => left.time_block - right.time_block)
          .map((row) => ({
            primary: `Block ${row.time_block}`,
            secondary: `${this.toNumber(row.energy_mw).toFixed(2)} MW planned discharge`,
          }));
    const idleRecords = asset
      ? asset.blocks
          .filter((block) => (block.action || '').toLowerCase() === 'idle')
          .map((block) => ({
            primary: `Block ${block.block_number} · ${block.block_label}`,
            secondary: `Idle forecast block${this.toNumber(block.market_price) > 0 ? ` at ₹${this.toNumber(block.market_price).toFixed(4)}/kWh` : ''}`,
          }))
      : scheduleRows
          .filter((row) => (row.action || '').toLowerCase() === 'idle')
          .sort((left, right) => left.time_block - right.time_block)
          .map((row) => ({
            primary: `Block ${row.time_block}`,
            secondary: 'Idle schedule block',
          }));
    const submittedRecords = scheduleRows
      .filter((row) => (row.status || '').toLowerCase() === 'submitted')
      .sort((left, right) => left.time_block - right.time_block)
      .map((row) => ({
        primary: `Block ${row.time_block} · ${this.normalizeActionLabel(row.action)}`,
        secondary: `${this.toNumber(row.energy_mw).toFixed(2)} MW submitted${this.toNumber(row.market_price) > 0 ? ` at ₹${this.toNumber(row.market_price).toFixed(4)}/kWh` : ''}`,
      }));
    const executedRecords = scheduleRows
      .filter((row) => (row.status || '').toLowerCase() === 'executed')
      .sort((left, right) => left.time_block - right.time_block)
      .map((row) => ({
        primary: `Block ${row.time_block} · ${this.normalizeActionLabel(row.action)}`,
        secondary: `${this.toNumber(row.energy_mw).toFixed(2)} MW executed${this.toNumber(row.market_price) > 0 ? ` at ₹${this.toNumber(row.market_price).toFixed(4)}/kWh` : ''}`,
      }));
    const expectedBlocks = new Set(
      asset
        ? asset.blocks.filter((block) => this.isActiveAction(block.action)).map((block) => block.block_number)
        : scheduleRows.filter((row) => this.isActiveAction(row.action)).map((row) => row.time_block),
    );
    const loggedBlocks = new Set(logRows.map((row) => row.time_block));
    const alertRecords: DistributionRecord[] = [];
    for (const block of [...expectedBlocks].sort((left, right) => left - right)) {
      if (!loggedBlocks.has(block)) {
        alertRecords.push({
          primary: `Block ${block}`,
          secondary: `${assetName}: expected forecast or schedule activity exists, but no SOC log was recorded for this block.`,
        });
      }
    }
    for (const block of [...loggedBlocks].sort((left, right) => left - right)) {
      if (!expectedBlocks.has(block)) {
        alertRecords.push({
          primary: `Block ${block}`,
          secondary: `${assetName}: SOC activity was logged for this block, but no matching forecast or schedule activity was found.`,
        });
      }
    }

    return [
      { label: 'charge', detail: 'charge forecast blocks', count: chargeRecords.length, tone: 'green', records: chargeRecords },
      { label: 'discharge', detail: 'discharge forecast blocks', count: dischargeRecords.length, tone: 'blue', records: dischargeRecords },
      { label: 'idle', detail: 'idle blocks', count: idleRecords.length, tone: 'slate', records: idleRecords },
      { label: 'submitted', detail: 'submitted schedule blocks', count: submittedRecords.length, tone: 'orange', records: submittedRecords },
      { label: 'executed', detail: 'executed schedule blocks', count: executedRecords.length, tone: 'amber', records: executedRecords },
      { label: 'alerts', detail: 'block mismatch alerts', count: alertRecords.length, tone: 'red', records: alertRecords },
    ];
  }

  private isActiveAction(action: string | null | undefined): boolean {
    const normalized = (action || '').toLowerCase();
    return normalized === 'charge' || normalized === 'discharge';
  }

  private toNumber(value: string | number | null | undefined): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private normalizeActionLabel(action: string | null | undefined): string {
    const normalized = (action || '').toLowerCase();
    if (normalized === 'charge') return 'Charge';
    if (normalized === 'discharge') return 'Discharge';
    if (normalized === 'idle') return 'Idle';
    return 'Unknown';
  }

  private blockActionLabel(rows: BessSchedule[]): string {
    const actions = [...new Set(rows.map((row) => this.normalizeActionLabel(row.action)).filter((value) => value !== 'Unknown'))];
    return actions.length ? actions.join(' / ') : 'Idle';
  }

  private blockActualQuantum(scheduledQuantumMw: number, block: number): number {
    if (scheduledQuantumMw <= 0) return 0;
    const variationFactor = ((block % 11) - 5) / 100;
    return Number((scheduledQuantumMw * (1 + variationFactor)).toFixed(2));
  }

  private inclusiveDateDays(fromDate: string | null | undefined, toDate: string | null | undefined): number {
    const start = this.parseDateOnly(fromDate);
    const end = this.parseDateOnly(toDate);
    if (!start || !end) return 0;
    const diffMs = end.getTime() - start.getTime();
    if (diffMs < 0) return 0;
    return Math.floor(diffMs / 86400000) + 1;
  }

  private parseDateOnly(value: string | null | undefined): Date | null {
    if (!value) return null;
    const [year, month, day] = String(value).trim().split('-').map((part) => Number(part));
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  }

  private timeDifferenceHours(fromTime: string | null | undefined, toTime: string | null | undefined): number {
    const fromMinutes = this.parseTimeToMinutes(fromTime, 0);
    const toMinutes = this.parseTimeToMinutes(toTime, 24 * 60);
    if (toMinutes <= fromMinutes) return 0;
    return (toMinutes - fromMinutes) / 60;
  }

  private parseTimeToMinutes(value: string | null | undefined, fallback: number): number {
    if (!value) return fallback;
    const match = /^(\d{2}):(\d{2})$/.exec(String(value).trim());
    if (!match) return fallback;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes) || minutes < 0 || minutes >= 60) return fallback;
    if (hours === 24 && minutes === 0) return 24 * 60;
    if (hours < 0 || hours > 23) return fallback;
    return hours * 60 + minutes;
  }

  protected selectedDate(): string {
    return this.selectedFilterDate();
  }

  protected selectedDateDisplayLabel(): string {
    const [year, month, day] = this.selectedDate().split('-');
    const monthLabel = this.monthOptions.find((option) => option.value === month)?.label || month;
    return `${day} ${monthLabel} ${year}`;
  }

  protected todayDate(): string {
    return this.todayIsoDate();
  }

  protected selectedMonthLabel(): string {
    return this.monthOptions.find((option) => option.value === this.selectedMonth())?.label || this.selectedMonth();
  }

  protected selectedMonthShortLabel(): string {
    return this.selectedMonthLabel().slice(0, 3).toLowerCase();
  }

  protected selectedMonthRangeLabel(): string {
    const [monthStart, monthEnd] = this.selectedMonthDateRange();
    return `${monthStart} to ${monthEnd}`;
  }

  private buildYearOptions(): string[] {
    const currentYear = Number(this.currentYear());
    return Array.from({ length: 7 }, (_, index) => String(currentYear - 3 + index));
  }

  private normalizeMonthValue(value: string | null | undefined): string {
    const normalized = String(value || '').padStart(2, '0');
    return this.monthOptions.some((option) => option.value === normalized) ? normalized : this.currentMonthValue();
  }

  private syncPeriodFromDate(value: string): void {
    const normalized = this.normalizeDateValue(value);
    this.selectedFilterDate.set(normalized);
    const [year, month] = normalized.split('-');
    if (year) this.selectedYear.set(year);
    if (month) this.selectedMonth.set(this.normalizeMonthValue(month));
  }

  private selectedMonthDateRange(): [string, string] {
    const monthStart = `${this.selectedYear()}-${this.selectedMonth()}-01`;
    const end = new Date(Number(this.selectedYear()), Number(this.selectedMonth()), 0);
    const monthEnd = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
    return [monthStart, monthEnd];
  }

  private dateRange(fromDate: string, toDate: string): string[] {
    const result: string[] = [];
    const cursor = new Date(`${fromDate}T00:00:00`);
    const end = new Date(`${toDate}T00:00:00`);
    while (cursor <= end) {
      result.push(this.formatLocalDate(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return result;
  }

  private currentYear(): string {
    return this.todayIsoDate().slice(0, 4);
  }

  private currentMonthValue(): string {
    return this.todayIsoDate().slice(5, 7);
  }

  private normalizeDateValue(value: string | null | undefined): string {
    const normalized = String(value || '').trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(normalized) ? normalized : this.todayIsoDate();
  }

  private buildDateFromParts(year: string, month: string, day: string): string {
    return `${year}-${month}-${day}`;
  }

  private todayIsoDate(): string {
    return this.formatLocalDate(new Date());
  }

  private formatLocalDate(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private blockTimeLabel(block: number): string {
    const startMinutes = (block - 1) * 15;
    const endMinutes = startMinutes + 15;
    return `${this.minutesToClock(startMinutes)} - ${this.minutesToClock(endMinutes)}`;
  }

  private minutesToClock(totalMinutes: number): string {
    const normalized = totalMinutes % (24 * 60);
    const hours = Math.floor(normalized / 60);
    const minutes = normalized % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }
}
